import React, { Fragment, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { UserConsumer } from '../context/userContext';

const copyStyles = (sourceDoc, targetDoc) => { // eslint-disable-line
  Array.from(sourceDoc.styleSheets).forEach(styleSheet => {
    if (!styleSheet.href || !styleSheet.href.includes('fastcompany')) return;

    if (styleSheet.cssRules) {
      // for <style> elements
      const newStyleEl = sourceDoc.createElement('style');

      Array.from(styleSheet.cssRules).forEach(cssRule => {
        // write the text of each rule into the body of the style element
        newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
      });

      targetDoc.head.appendChild(newStyleEl);
    } else if (styleSheet.href) {
      // for <link> elements loading CSS from a URL
      const newLinkEl = sourceDoc.createElement('link');

      newLinkEl.rel = 'stylesheet';
      newLinkEl.href = styleSheet.href;
      targetDoc.head.appendChild(newLinkEl);
    }
  });
};
class PopOutPortal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { win: null, el: null };
  }

  componentDidMount() {
    this.setupPopoutPortal();
  }

  componentWillUnmount() {
    this.state.win.close();
  }

  setupPopoutPortal() {
    const win = window.open('', '', 'width=823,height=620');
    copyStyles(document, win.document);
    win.document.title = 'A React portal window';
    const el = document.createElement('div');
    win.document.body.appendChild(el);
    this.setState({ win, el });
  }

  render() {
    const { el } = this.state;
    if (!el) {
      return null;
    }
    return ReactDOM.createPortal(this.props.children, el);
  }
}

PopOutPortal.propTypes = {
  children: PropTypes.arrayOf(PropTypes.shape([])).isRequired
};

const VisualDebugger = ({ trackedDataObject }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [currentItems, setCurrentItems] = useState([]); // eslint-disable-line
  const [showWindowPortal, setShowWindowPortal] = useState(false);
  // const [format, setFormat] = useState('raw'); // Display format i.e. `raw` vs. `table` vs. ???

  const isUniquePageView = () => {
    const currentInfo = JSON.parse(localStorage.getItem('_history') || '[]');

    return (
      currentInfo.filter(
        view => view.location === get(window, 'location.pathname')
      ).length <= 0
    );
  };

  const handleInitialPageLoad = () => {
    const currentInfo = localStorage.getItem('_history') || '[]';
    const mainObj = JSON.parse(currentInfo);
    const currentDate = Date.now();
    const isUnique = isUniquePageView();

    const newPageInfo = {
      label: new Date(currentDate).toLocaleString('en-US', { hour12: true }),
      name: document.title || 'Default Page Visit',
      location: get(window, 'location.pathname', 'Unknown Location'),
      date: currentDate,
      isUnique
    };

    mainObj.push(newPageInfo);
    localStorage.setItem('_history', JSON.stringify(mainObj));

    setCurrentItems(currentDate);
  };

  const trackNewPageView = () => {
    const currentInfo = localStorage.getItem('_history') || '[]';
    const mainObj = JSON.parse(currentInfo);
    const currentDate = Date.now();
    const isUnique = isUniquePageView();

    const newPageInfo = {
      label: new Date(currentDate).toLocaleString('en-US', { hour12: true }),
      name: document.title || 'Default Page Visit',
      location: get(window, 'location.pathname', 'Unknown Location'),
      date: currentDate,
      isUnique
    };
    mainObj.push(newPageInfo);
    localStorage.setItem('_history', JSON.stringify(mainObj));

    setCurrentItems(currentDate);
  };

  const listenForElementMutation = element => {
    new MutationObserver(mutations => { // eslint-disable-line
      trackNewPageView();
    }).observe(document.querySelector(element), {
      subtree: true,
      characterData: true,
      childList: true
    });
  };

  const togglePopOutWindow = () => {
    setShowWindowPortal(!showWindowPortal);
  };

  const closePopOutWindowRoot = () => {
    setShowWindowPortal(false);
  };

  useEffect(() => {
    // Append vists to `pageVisits` array on PAGE LOAD
    handleInitialPageLoad();

    // Listen for title tag change, for SPA tracking
    listenForElementMutation('title');
  }, []); // eslint-disable-line

  const tableizeObject = (object, name = 'Data') => (
    <details open>
      <summary className="tableDataName">{startCase(name)}</summary>
      <table aria-label="simple table">
        <tr>
          {Object.keys(object).map((item, i) => (
            <th key={`debug-table-header-${i}`}>{item}</th>
          ))}
        </tr>
        <tr>
          {Object.values(object).map((item, i) => {
            let value = item;
            if (typeof item === 'object' || typeof item === 'boolean') {
              value = JSON.stringify(item);
            }
            return <td key={`debug-table-data-${i}`}>{value}</td>;
          })}
        </tr>
      </table>
    </details>
  );

  const localHistory = JSON.parse(localStorage.getItem('_history') || '[]');

  return (
    <Fragment>
      <div id="InlineDebugger" className={collapsed && 'collapsed'}>
        <span
          className="displayToggle"
          onClick={e => {
            e.preventDefault();
            setCollapsed(!collapsed);
          }}
        >
          {collapsed ? '▲' : 'X'}
        </span>
        {!collapsed &&
          !showWindowPortal && (
            <div className="debugData">
              {Object.keys(trackedDataObject).map((data, i) => {
                const dataValues = Object.values(trackedDataObject)[i];
                if (isEmpty(dataValues)) return null; // If empty object, return nothing
                return (
                  <div key={`data-object-${i}`} className="tableDataContainer">
                    {tableizeObject(dataValues, data)}
                  </div>
                );
              })}
            </div>
          )}
        {!collapsed && (
          <div className="debugButtons">
            <UserConsumer>
              {({ flushAllCookies }) => ( // eslint-disable-line 
                <Fragment>
                  <button
                    className="debugButton"
                    onClick={() => {
                      togglePopOutWindow();
                    }}
                  >
                    {showWindowPortal ? 'Close the' : 'Open as '} window
                  </button>
                  <button
                    className="debugButton"
                    onClick={() => {
                      flushAllCookies();
                    }}
                  >
                    Reset My Cookies
                  </button>
                </Fragment>
              )}
            </UserConsumer>
          </div>
        )}
        {!collapsed &&
          !showWindowPortal && (
            <div className="debugHistory">
              {localHistory.reverse().map(item => (
                // <div>{item.name}</div>
                <details>
                  <summary className="tableDataName">{`${
                    item.isUnique ? '🚨' : '✅'
                  } ${item.label} - ${item.location}`}</summary>
                  <div>
                    <div>{item.name}</div>
                  </div>
                </details>
              ))}
            </div>
          )}
      </div>

      {/* ------------ THE PORTAL THAT OPENS! ---------------- */}
      {showWindowPortal && (
        <PopOutPortal>
          <div
            id="InlineDebugger"
            className={`InlineDebugger-portal ${collapsed && 'collapsed'}`}
          >
            <span
              className="displayToggle"
              onClick={e => {
                e.preventDefault();
                setCollapsed(!collapsed);
              }}
            >
              {collapsed ? '▲' : 'X'}
            </span>
            {!collapsed && (
              <div className="debugData">
                {Object.keys(trackedDataObject).map((data, i) => {
                  const dataValues = Object.values(trackedDataObject)[i];
                  if (isEmpty(dataValues)) return null; // If empty object, return nothing
                  return (
                    <div
                      key={`data-object-${i}`}
                      className="tableDataContainer"
                    >
                      {tableizeObject(dataValues, data)}
                    </div>
                  );
                })}
              </div>
            )}
            {!collapsed && (
              <div className="debugButtons">
                <UserConsumer>
                  {({ resetExperienceCookie }) => ( // eslint-disable-line
                    <Fragment>
                      <button
                        className="debugButton"
                        onClick={() => {
                          closePopOutWindowRoot();
                        }}
                      >
                        {showWindowPortal ? 'Close the' : 'Open as '} window
                      </button>
                      <button
                        className="debugButton"
                        onClick={() => {
                          resetExperienceCookie();
                        }}
                      >
                        Reset My Cookies
                      </button>
                    </Fragment>
                  )}
                </UserConsumer>
              </div>
            )}
            {!collapsed && (
              <div className="debugHistory">
                {localHistory.reverse().map(item => (
                  // <div>{item.name}</div>
                  <details>
                    <summary className="tableDataName">{`${
                      item.isUnique ? '🚨' : '✅'
                    } ${item.label} - ${item.location}`}</summary>
                    <div>
                      <div>{item.name}</div>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </PopOutPortal>
      )}
    </Fragment>
  );
};

VisualDebugger.defaultProps = {
  trackedDataObject: {}
};

VisualDebugger.propTypes = {
  trackedDataObject: PropTypes.arrayOf(PropTypes.shape({}))
};

export default VisualDebugger;
